import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";


interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showOurPart?: boolean;
    showMfrPart?: boolean;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    partContainer?: InjectableCss;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 0 },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            display: block;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;            
                font-family: Roboto;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .028rem; 
                margin-bottom: .31250625rem;
            }
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            display: block;
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;
            
            @media screen and (max-width: 1399px) {
                display: block;
                margin-bottom: .31250625rem;      
                   
            }
        `,
    },
    erpNumberGridItem: {
        css: css`
            display: block;
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-bottom: .31250625rem;    
            }

            @media screen and (min-width: 1400px) {
                max-width: 7.5rem;
            }
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            display: block;
            flex: 0 0 auto;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;
                color: #002554;
                font-family: Roboto;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .028rem; 
                margin-bottom: .31250625rem;             
            }
        `,
    },
    customerNameValueText: {
        css: css`
            display: block;
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;
            
            @media screen and (max-width: 1399px) {
                display: block;
                margin-bottom: .31250625rem;     
                          
            }
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            display: block;
            flex: 0 0 auto;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;
                color: #002554;
                font-family: Roboto;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .028rem;
                margin-bottom: .31250625rem;              
            }
        `,
    },
    manufacturerItemValueText: {
        css: css`
            display: block;
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .028rem;
            line-height: 1.25rem;
            
            @media screen and (max-width: 1399px) {
                display: block;
                margin-bottom: .31250625rem;      
            }
        `,
    },
    partContainer: {
        css: css`
            display: block;
            @media screen and (min-width: 1400px) {
                max-width: 7.5rem;
            }
        `,
    }
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showOurPart = true,
    showMfrPart = true,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            {showMfrPart && displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem} {...styles.partContainer} className="mfg-part-container">
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText}>{manufacturerItem}</Typography>
                </GridItem>
            )}
            {showOurPart && (
                <>
                    <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem} className="part-number-container">
                        {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                            {productNumber}
                        </Typography>
                    </GridItem>
                    {displayCustomerNumber && (
                        <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem} {...styles.partContainer} className="customer-part-container">
                            {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                            {!showLabel && (
                                <VisuallyHidden>
                                    <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                                </VisuallyHidden>
                            )}
                            <Typography {...styles.customerNameValueText}>{customerProductNumber}</Typography>
                        </GridItem>
                    )}
                </>
            )}
        </GridContainer>
    );
};

export default ProductPartNumbers;
